import React, { useEffect, useState } from 'react';
import { resolveImageUrl } from '../../utils/image';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Box,
  Chip,
  Rating,
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Favorite as FavoriteIcon,
  ArrowForward as ArrowForwardIcon,
  LocalShipping as ShippingIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Product } from '../../types';
import Button from './Button';
import PriceDisplay from './PriceDisplay';
import { useAppSelector } from '../../hooks';
import useStoreSettings from '../../hooks/useStoreSettings';
import { ROUTES } from '../../constants';

const StyledCard = styled(Card)(() => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.22s ease-in-out, box-shadow 0.22s ease-in-out',
  borderRadius: 18,
  overflow: 'hidden',
  background:
    'linear-gradient(180deg, rgba(255, 251, 253, 0.95) 0%, #ffffff 38%, #ffffff 100%)',
  boxShadow: '0 12px 34px rgba(25, 18, 30, 0.06)',
  border: '1px solid rgba(36, 24, 38, 0.06)',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 16px 36px rgba(25, 18, 30, 0.12)',
  },
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  height: 212,
  [theme.breakpoints.up('sm')]: {
    height: 228
  },
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  animation: 'productImageFade 320ms ease',
  '@keyframes productImageFade': {
    from: { opacity: 0.78, transform: 'scale(1.01)' },
    to: { opacity: 1, transform: 'scale(1)' }
  }
}));

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product) => void;
  onAddToWishlist: (product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onAddToCart,
  onAddToWishlist,
}) => {
  const navigate = useNavigate();
  const store = useStoreSettings();
  // Only promise free delivery when the store rules actually give it
  const freeDeliveryLabel = !store
    ? null
    : store.shippingCharge <= 0
      ? 'Free delivery'
      : store.freeShippingThreshold > 0
        ? `Free delivery above ₹${store.freeShippingThreshold}`
        : null;
  const HOVER_ROTATE_INTERVAL_MS = 1800;
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const hasMultipleImages = product.images.length > 1;
  const primaryImage =
    product.images[activeImageIndex] || product.images[0] || '';
  const isOutOfStock = product.stock <= 0;
  const isWishlisted = useAppSelector((state) =>
    (state.wishlist.wishlist?.items || []).some((item) => item.product?._id === product._id)
  );
  const isInCart = useAppSelector((state) => {
    const serverMatch = (state.cart.cart?.items || []).some((item) => item.product?._id === product._id);
    const guestMatch = state.cart.guestItems.some((item) => item.product?._id === product._id);
    return serverMatch || guestMatch;
  });

  useEffect(() => {
    if (!hasMultipleImages || !isHovered) return;

    const interval = setInterval(() => {
      setActiveImageIndex((prev) =>
        prev + 1 < product.images.length ? prev + 1 : 0
      );
    }, HOVER_ROTATE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [HOVER_ROTATE_INTERVAL_MS, hasMultipleImages, isHovered, product.images.length]);

  return (
    <Box
      component={RouterLink}
      to={`/products/${product.slug || product._id}`}
      sx={{ textDecoration: 'none', color: 'inherit' }}
    >
      <StyledCard
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box sx={{ position: 'relative' }}>
          <StyledCardMedia
            key={`${product._id}-${activeImageIndex}`}
            image={resolveImageUrl(primaryImage)}
            title={product.name}
          />
          {/* Low stock badge */}
          {!isOutOfStock && product.stock <= 5 && (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                bgcolor: '#ff6b35',
                color: '#fff',
                px: 1.25,
                py: 0.3,
                borderRadius: 99,
                fontSize: '0.68rem',
                fontWeight: 700,
              }}
            >
              Only {product.stock} left
            </Box>
          )}
          {/* Out of stock overlay */}
          {isOutOfStock && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  bgcolor: 'rgba(0,0,0,0.72)',
                  color: '#fff',
                  px: 2,
                  py: 0.6,
                  borderRadius: 99,
                  fontSize: '0.78rem',
                  fontWeight: 600,
                }}
              >
                Sold out
              </Typography>
            </Box>
          )}
        </Box>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pb: 1 }}>
        {/* Name + Category */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75, gap: 1 }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '0.92rem', sm: '0.98rem' },
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {product.name}
          </Typography>
          {product.category?.name?.trim() && (
            <Chip
              label={product.category.name}
              size="small"
              sx={{
                bgcolor: 'rgba(112, 97, 218, 0.14)',
                color: 'secondary.dark',
                fontSize: '0.68rem',
                flexShrink: 0,
              }}
            />
          )}
        </Box>

        {/* Rating (only once approved reviews exist) */}
        {Boolean(product.ratingCount) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Rating value={product.ratingAverage || 0} precision={0.1} readOnly size="small" />
            <Typography variant="caption" color="text.secondary">
              ({product.ratingCount})
            </Typography>
          </Box>
        )}

        {/* Material */}
        {product.material && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            {product.material}
          </Typography>
        )}

        {/* Price + Stock */}
        <Box sx={{ mt: 'auto' }}>
          <PriceDisplay item={product} priceVariant="h6" mrpVariant="body2" sx={{ mb: 0.5 }} />
          {!isOutOfStock && freeDeliveryLabel && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ShippingIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {freeDeliveryLabel}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between', gap: 1 }}>
        <Button
          variant="contained"
          startIcon={isInCart ? <ArrowForwardIcon /> : <CartIcon />}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isInCart) {
              navigate(ROUTES.CART);
              return;
            }
            onAddToCart(product);
          }}
          disabled={!isInCart && isOutOfStock}
          sx={{
            flex: 1,
            whiteSpace: 'nowrap',
            fontSize: { xs: '0.88rem', sm: '0.95rem' },
            minHeight: 42
          }}
        >
          {isInCart ? 'Go to Cart' : isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
        </Button>
        <IconButton
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddToWishlist(product);
          }}
          color={isWishlisted ? 'secondary' : 'default'}
        >
          {isWishlisted ? <FavoriteIcon /> : <FavoriteBorderIcon />}
        </IconButton>
      </CardActions>
      </StyledCard>
    </Box>
  );
};

export default ProductCard;
